import { useEffect, useRef } from "react";
import { descendMine, leaveMine, mineAttack, mineBlock, stepMine } from "../systems/mine";
import type { GameState } from "../game/types";
import type { MineControls } from "../systems/mine";
import { useTouchCapability, VirtualJoystick } from "./VirtualJoystick";
import { drawPlayerMine } from "../render/characterSprites";
import { drawEnemyMine } from "../render/enemySprites";
import { drawMineBackground, drawMineBlock, drawMineExit, drawMineProjectile } from "../render/mineSprites";

const TILE = 40;
export function MineCanvas({ state, onChange }: { state: GameState; onChange: (state: GameState) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef(state);
  const controls = useRef<MineControls>({ left: false, right: false, jump: false, dash: false });
  const touchCapable = useTouchCapability();
  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (["a", "arrowleft"].includes(key)) controls.current.left = true;
      if (["d", "arrowright"].includes(key)) controls.current.right = true;
      if (["w", "arrowup", " "].includes(key)) controls.current.jump = true;
      if (key === "shift") controls.current.dash = true;
      if (key === "j") onChange(mineAttack(stateRef.current, "attack"));
      if (key === "k") onChange(mineBlock(stateRef.current));
      if (key === "l") onChange(mineAttack(stateRef.current, "special"));
      if (key === "e") onChange(descendMine(stateRef.current));
      if (key === "escape") onChange(leaveMine(stateRef.current));
      if (["arrowleft", "arrowright", "arrowup", " "].includes(key)) event.preventDefault();
    };
    const up = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (["a", "arrowleft"].includes(key)) controls.current.left = false;
      if (["d", "arrowright"].includes(key)) controls.current.right = false;
      if (["w", "arrowup", " "].includes(key)) controls.current.jump = false;
      if (key === "shift") controls.current.dash = false;
    };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [onChange]);

  useEffect(() => {
    let stopped = false; let last = performance.now();
    const tick = (now: number) => {
      if (stopped) return;
      const dt = Math.min(.045, (now - last) / 1000); last = now;
      const next = stepMine(stateRef.current, controls.current, dt);
      controls.current.dash = false;
      stateRef.current = next; onChange(next);
      window.setTimeout(() => requestAnimationFrame(tick), 24);
    };
    requestAnimationFrame(tick); return () => { stopped = true; };
  }, [onChange]);

  useEffect(() => {
    let frame = 0;
    const render = () => {
      const canvas = canvasRef.current; const current = stateRef.current; const mine = current.mine;
      if (!canvas || !mine) return;
      const ctx = canvas.getContext("2d"); if (!ctx) return;
      const ratio = Math.min(2, window.devicePixelRatio || 1); const w = canvas.clientWidth; const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * ratio) || canvas.height !== Math.round(h * ratio)) { canvas.width = Math.round(w * ratio); canvas.height = Math.round(h * ratio); }
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0); ctx.imageSmoothingEnabled = false;
      const cameraX = Math.max(0, Math.min(mine.width * TILE - w, mine.player.x * TILE - w * .42));
      const cameraY = Math.max(0, Math.min(mine.height * TILE - h, mine.player.y * TILE - h * .58));
      const tierColors = [["#17251e", "#2f4532"], ["#23272a", "#40474a"], ["#301d1b", "#5b3025"], ["#1e2638", "#35435d"], ["#24202d", "#473b51"]][mine.tier - 1];
      ctx.fillStyle = tierColors[0]; ctx.fillRect(0, 0, w, h);
      ctx.save(); ctx.translate(-cameraX, -cameraY);
      drawMineBackground(ctx, mine.tier, mine.width * TILE, mine.height * TILE, performance.now());
      ctx.fillStyle = `${tierColors[1]}55`; ctx.fillRect(0, 0, mine.width * TILE, mine.height * TILE);
      for (const block of mine.blocks) if (block.solid) drawMineBlock(ctx, block, TILE);
      drawMineExit(ctx, mine.exitX, mine.exitY, mine.tier, TILE);
      for (const enemy of mine.enemies) if (enemy.alive) {
        const ex = enemy.x * TILE; const ey = enemy.y * TILE;
        ctx.save(); ctx.translate(ex, ey + 16); ctx.scale(1.22, 1.22); ctx.translate(-ex, -ey - 16);
        drawEnemyMine(ctx, enemy.enemyId, ex, ey, enemy.direction, enemy.hurt, enemy.hp, enemy.maxHp, performance.now());
        ctx.restore();
      }
      for (const projectile of mine.projectiles) drawMineProjectile(ctx, projectile.x * TILE, projectile.y * TILE, projectile.color, projectile.friendly, performance.now());
      drawPlayerMine(ctx, current, mine.player.x * TILE, mine.player.y * TILE, mine.player.facing, mine.player.invulnerable, performance.now() / 120);
      ctx.restore();
      const gradient = ctx.createRadialGradient(w * .5, h * .45, 70, w * .5, h * .45, Math.max(w, h) * .8); gradient.addColorStop(0, "rgba(0,0,0,0)"); gradient.addColorStop(1, "rgba(0,0,0,.55)"); ctx.fillStyle = gradient; ctx.fillRect(0, 0, w, h);
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render); return () => cancelAnimationFrame(frame);
  }, []);


  const mine = state.mine;
  return <div className="canvas-wrap mine-canvas-wrap">
    <canvas ref={canvasRef} aria-label={`Mine tier ${mine?.tier ?? 1}, depth ${mine?.depth ?? 1}`} />
    <div className="mine-overlay top-left"><b>STRATUM {mine?.tier}</b><span>Depth {mine?.depth}</span><span>Ore {mine?.mined} · Kills {mine?.kills}</span></div>
    <div className="mine-overlay top-right"><span>J attack · K mine · L technique</span><span>Shift dash · E descend · Esc leave</span></div>
    <div className="mine-message">{mine?.message}</div>
    {touchCapable && <div className="mine-touch-controls">
      <VirtualJoystick label="Mine movement joystick" onVector={(x, y, active) => {
        controls.current.left = active && x < -.22;
        controls.current.right = active && x > .22;
        controls.current.jump = active && y < -.48;
      }} />
      <div className="mine-action-cluster">
        <button onClick={() => onChange(mineAttack(stateRef.current, "attack"))}>ATK</button>
        <button onClick={() => onChange(mineBlock(stateRef.current))}>MINE</button>
        <button onClick={() => onChange(mineAttack(stateRef.current, "special"))}>TECH</button>
        <button onClick={() => { controls.current.dash = true; }}>DASH</button>
        <button onClick={() => onChange(descendMine(stateRef.current))}>DOWN</button>
      </div>
    </div>}
  </div>;
}
